/**
 * Session identity + localStorage plumbing.
 *
 * Phase 0 uses an anonymous session UUID generated on first load (or when
 * the user resets the session). PII never leaves the browser before the
 * final `submit` action. See ADR-006 "Client-owned state".
 */

import { v4 as uuidv4 } from 'uuid';
import type { IntakeState } from '../api/types';

const SESSION_KEY = 'cf-intake-session-id';
const STATE_KEY_PREFIX = 'cf-intake-state:';
const PHASE_KEY_PREFIX = 'cf-intake-phase:';

const VALID_PHASES = new Set([
  'welcome',
  'upload',
  'chat',
  'review',
  'submitted',
]);

/** Pristine IntakeState — empty buckets, empty history, no language yet.
 *  required_pending / optional_pending are empty until the Worker returns
 *  them in a state_delta (the Worker seeds them from the program config). */
export const INITIAL_STATE: IntakeState = Object.freeze({
  confirmed: Object.freeze({}) as Record<string, unknown>,
  required_pending: Object.freeze([]) as unknown as string[],
  optional_pending: Object.freeze([]) as unknown as string[],
  history: Object.freeze([]) as unknown as IntakeState['history'],
});

function stateKey(sessionId: string): string {
  return `${STATE_KEY_PREFIX}${sessionId}`;
}

/** Load the existing session id from localStorage, or mint a new one. */
export function loadOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing && existing.length > 0) return existing;
    const fresh = uuidv4();
    localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // localStorage blocked (privacy mode, iframe sandbox). Fall back to an
    // in-memory UUID so the session still works for this page load.
    return uuidv4();
  }
}

/** Load saved state for this session, or null if none / unparseable. */
export function loadState(sessionId: string): IntakeState | null {
  try {
    const raw = localStorage.getItem(stateKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isIntakeState(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist state to localStorage. Silently swallows errors (private mode). */
export function saveState(sessionId: string, state: IntakeState): void {
  try {
    localStorage.setItem(stateKey(sessionId), JSON.stringify(state));
  } catch {
    /* storage quota exceeded or disabled — nothing useful to do */
  }
}

/** Wipe session + state + phase for a clean restart. */
export function resetSession(sessionId: string): string {
  try {
    localStorage.removeItem(stateKey(sessionId));
    localStorage.removeItem(phaseKey(sessionId));
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  return loadOrCreateSessionId();
}

function phaseKey(sessionId: string): string {
  return `${PHASE_KEY_PREFIX}${sessionId}`;
}

export type StoredPhase = 'welcome' | 'upload' | 'chat' | 'review' | 'submitted';

export function loadPhase(sessionId: string): StoredPhase | null {
  try {
    const raw = localStorage.getItem(phaseKey(sessionId));
    if (raw && VALID_PHASES.has(raw)) return raw as StoredPhase;
    return null;
  } catch {
    return null;
  }
}

export function savePhase(sessionId: string, phase: string): void {
  try {
    localStorage.setItem(phaseKey(sessionId), phase);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Narrow guard — minimal structural check. The Worker is the source of truth
// for strict validation (Zod); this keeps the client defensive against
// corrupted localStorage entries.
// ---------------------------------------------------------------------------

function isIntakeState(v: unknown): v is IntakeState {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    o.confirmed !== null &&
    typeof o.confirmed === 'object' &&
    Array.isArray(o.required_pending) &&
    Array.isArray(o.optional_pending) &&
    Array.isArray(o.history)
  );
}
